import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { exportToCsv } from "@/lib/csv";
import { allocType } from "@/pages/reseller/Allocations";

const toIso = (v) => (v ? new Date(v).toISOString() : undefined);
const fmt = (iso) => new Date(iso).toLocaleString();

export const SubAgentHistoryDialog = ({ sub, onClose }) => {
  const [rows, setRows] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => {
    if (!sub) return;
    api.get(`/reseller/subagents/${sub.id}/recharges`, { params: { date_from: toIso(start), date_to: toIso(end) } }).then((r) => setRows(r.data.recharges)).catch(() => {});
  }, [sub, start, end]);
  useEffect(() => { if (sub) api.get(`/reseller/subagents/${sub.id}/allocations`).then((r) => setAllocs(r.data.allocations)).catch(() => {}); }, [sub]);
  if (!sub) return null;
  const csv = () => exportToCsv(`subagent-${sub.name}-recharges`, rows, [
    { header: "Timestamp", accessor: (r) => fmt(r.created_at) }, { header: "Platform", accessor: (r) => r.platform_name },
    { header: "Recipient", accessor: (r) => r.recipient_uid }, { header: "Diamonds", accessor: (r) => r.diamonds },
    { header: "Balance after", accessor: (r) => r.balance_after }, { header: "Status", accessor: (r) => r.status },
  ]);
  const inp = "h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white";
  return (
    <Dialog open={!!sub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="subagent-history-dialog">
        <DialogHeader><DialogTitle>{sub.name} · history</DialogTitle></DialogHeader>
        <Tabs defaultValue="recharges">
          <TabsList><TabsTrigger value="recharges" data-testid="subagent-history-tab-recharges">Recharges</TabsTrigger><TabsTrigger value="allocs" data-testid="subagent-history-tab-allocs">Diamond transfers</TabsTrigger></TabsList>
          <TabsContent value="recharges">
            <div className="flex flex-wrap items-center gap-2 my-2">
              <input type="datetime-local" className={inp} value={start} onChange={(e) => setStart(e.target.value)} data-testid="subagent-history-start" />
              <span className="text-xs text-zinc-500">to</span>
              <input type="datetime-local" className={inp} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="subagent-history-end" />
              <button onClick={csv} className="ml-auto h-8 px-3 text-xs rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 font-medium" data-testid="subagent-history-csv">Download CSV</button>
            </div>
            <Table rows={rows} cols={[["Time", (r) => fmt(r.created_at)], ["Platform", (r) => r.platform_name], ["Recipient", (r) => r.recipient_uid], ["Diamonds", (r) => r.diamonds], ["Balance after", (r) => r.balance_after ?? "—"], ["Status", (r) => r.status]]} empty="No recharges in this range" />
          </TabsContent>
          <TabsContent value="allocs">
            <Table rows={allocs} cols={[["Time", (a) => fmt(a.created_at)], ["Platform", (a) => a.platform_name], ["Diamonds", (a) => (a.amount > 0 ? `+${a.amount}` : a.amount)], ["Type", allocType], ["Balance after", (a) => a.balance_after ?? "—"]]} empty="No transfers yet" />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const Table = ({ rows, cols, empty }) => (
  <div className="overflow-x-auto rounded-md border border-zinc-200">
    <table className="w-full text-xs">
      <thead className="bg-zinc-50"><tr>{cols.map(([h]) => <th key={h} className="px-3 py-2 text-left font-semibold text-zinc-500">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => <tr key={r.id} className="border-t border-zinc-100">{cols.map(([h, fn]) => <td key={h} className="px-3 py-2 font-mono">{fn(r)}</td>)}</tr>)}
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-zinc-400">{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);
